import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios
axios.defaults.withCredentials = true;

// Renovación automática de sesión: si una petición devuelve 401,
// intenta refrescar el token y reintenta la petición original una vez.
let refreshPromise = null;
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const url = original?.url || '';
    if (error.response?.status === 401 && !original._retry && !url.includes('/api/auth/')) {
      original._retry = true;
      try {
        refreshPromise = refreshPromise || axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        await refreshPromise;
        refreshPromise = null;
        return axios(original);
      } catch (refreshError) {
        refreshPromise = null;
      }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: si volvemos del callback OAuth de Google, saltar el check /me.
    // AuthCallback intercambiará el session_id y establecerá la sesión primero.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(
      `${API_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const loginWithGoogle = async (sessionId) => {
    const response = await axios.post(
      `${API_URL}/api/auth/google/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name, referralCodeUsed = null, phone = null) => {
    const response = await axios.post(
      `${API_URL}/api/auth/register`,
      { email, password, name, referral_code_used: referralCodeUsed, phone },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Silent fail - user will still be logged out locally
      console.debug('Logout API call failed:', error.message);
    }
    setUser(false);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  const value = {
    user,
    loading,
    login,
    loginWithGoogle,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
